import React, { useEffect, useMemo, useRef, useState } from 'react';
import { parseNodeRef, RefGroup, RefOption, RefSelection } from '../pnid/entityRef';

export interface RefComboboxProps {
  /** The stored ref string; shown while nothing is typed. */
  value: string;
  onChange: (selection: RefSelection) => void;
  groups: RefGroup[];
  /** The input's accessible name. */
  label: string;
  placeholder?: string;
  autoFocus?: boolean;

  onHighlightEntity?: (id: string | null) => void;
  /** The text typed and not yet picked, reported on every change, so a form's Save can commit it. */
  onDraftChange?: (text: string) => void;
  disabled?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
}

type Row =
  | { kind: 'option'; group: string; option: RefOption }
  | { kind: 'use'; text: string };

const matches = (option: RefOption, needle: string): boolean =>
  option.ref.toLowerCase().includes(needle)
  || option.label.toLowerCase().includes(needle)
  || (option.detail ?? '').toLowerCase().includes(needle);

const NODE_REF_MISSING = 'That line is not on this sheet yet. Mark it first, then pick it under Lines.';

const rowStyle = (active: boolean): React.CSSProperties => ({
  padding: '6px 10px',
  cursor: 'pointer',
  fontSize: 12,
  background: active ? 'var(--raven-state-hover)' : 'transparent',
});

const RefCombobox: React.FC<RefComboboxProps> = ({
  value, onChange, groups, label, placeholder = 'Type to search…', autoFocus = false,
  onHighlightEntity, onDraftChange, disabled = false, inputRef: inputRefProp,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const onDraftChangeRef = useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;
  useEffect(() => { onDraftChangeRef.current?.(query); }, [query]);
  const [highlightIdx, setHighlightIdx] = useState(0);
  // Set by an arrow key, so Enter on an empty query commits the chosen row and never the untouched first one.
  const [navigated, setNavigated] = useState(false);
  /**
   * DECISION(2026-10-26, Abhishek): A hovered row is lit and taken only by a click; Enter takes the row the keyboard set.
   * A list that opens under a resting pointer would otherwise hand that row to Enter without the SME moving the mouse.
   */
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const localInputRef = useRef<HTMLInputElement>(null);
  const inputRef = inputRefProp ?? localInputRef;
  const containerRef = useRef<HTMLDivElement>(null);
  const listId = React.useId();
  const optionId = (idx: number) => `${listId}-option-${idx}`;

  const needle = query.trim().toLowerCase();
  const nodeNo = parseNodeRef(query);

  const { rows, nodeRefMissing } = useMemo(() => {
    const all: Row[] = groups.flatMap((group) => group.options.map((option) => ({ kind: 'option' as const, group: group.name, option })));
    if (!needle) return { rows: all, nodeRefMissing: false };
    if (nodeNo !== null) {
      const line = all.find((row) => row.kind === 'option' && row.option.kind === 'node' && row.option.ref === `Node #${nodeNo}`);
      return { rows: line ? [line] : [], nodeRefMissing: !line };
    }
    const hits = all.filter((row) => row.kind === 'option' && matches(row.option, needle));
    // A pasted ref that equals an option's ref is that option: the Use row would lose its id and sheet.
    const exact = hits.some((row) => row.kind === 'option' && row.option.ref.toLowerCase() === needle);
    return { rows: exact ? hits : [...hits, { kind: 'use' as const, text: query.trim() }], nodeRefMissing: false };
  }, [groups, needle, nodeNo, query]);

  const listOpen = open && !disabled;
  const activeIdx = Math.min(highlightIdx, Math.max(rows.length - 1, 0));
  const activeRow: Row | undefined = rows[activeIdx];
  const activeId = listOpen && activeRow ? optionId(activeIdx) : undefined;

  useEffect(() => { setHighlightIdx(0); setNavigated(false); setHoveredIdx(null); }, [query, open]);

  // A field that turns read-only keeps no half-typed text and no open list.
  useEffect(() => {
    if (!disabled) return;
    setOpen(false);
    setQuery('');
  }, [disabled]);

  useEffect(() => {
    if (!autoFocus || disabled) return;
    const timer = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [autoFocus, disabled, inputRef]);

  // A click elsewhere closes the list and keeps the typed text, so another dock covering
  // the form does not erase a draft the SME has not finished.
  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        onHighlightEntity?.(null);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [onHighlightEntity]);

  // The keyboard moves the highlight; the list follows it, so Enter never commits a row out of view.
  useEffect(() => {
    if (!navigated || !activeId) return;
    document.getElementById(activeId)?.scrollIntoView?.({ block: 'nearest' });
  }, [navigated, activeId]);

  const pick = (row: Row) => {
    if (disabled) return;
    onChange(row.kind === 'use' ? { ref: row.text, kind: 'manual' } : row.option);
    setQuery('');
    setOpen(false);
    onHighlightEntity?.(null);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (event.key === 'Escape') {
      // An open list takes the Escape, so the editor around it stays open.
      if (open) event.preventDefault();
      setOpen(false);
      setQuery('');
      onHighlightEntity?.(null);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) setOpen(true);
      setNavigated(true);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setHighlightIdx((idx) => Math.min(Math.max(idx + step, 0), Math.max(rows.length - 1, 0)));
      return;
    }
    if (event.key !== 'Enter' && event.key !== 'Tab') return;
    if (event.key === 'Tab' && event.shiftKey) return;
    // Typed text or an arrow-chosen row is a choice. An untouched first row is not.
    const chosen = needle || navigated ? activeRow : undefined;
    if (chosen) {
      // Tab after a pick still moves focus to the next field.
      if (event.key === 'Enter') event.preventDefault();
      pick(chosen);
      return;
    }
    // Enter never submits the form around the field. Tab moves focus on its own.
    if (event.key === 'Enter') event.preventDefault();
    setOpen(false);
  };

  let rowIdx = -1;
  return (
    <div ref={containerRef}>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <input
          ref={inputRef}
          role="combobox"
          aria-label={label}
          aria-expanded={listOpen}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeId}
          value={listOpen || query ? query : value}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
          onFocus={() => { if (!disabled) setOpen(true); }}
          onKeyDown={onKeyDown}
          style={{ flex: 1, padding: 8, border: '1px solid var(--raven-divider)', borderRadius: 'var(--raven-radius-field)', fontSize: 12 }}
        />
        {value && !disabled && (
          <button
            type="button"
            aria-label={`Clear ${label}`}
            onClick={() => { onChange({ ref: '', kind: 'manual' }); setQuery(''); inputRef.current?.focus(); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--raven-text-secondary)', fontSize: 14, padding: '0 4px' }}
          >
            ×
          </button>
        )}
      </div>

      {/* The list takes its room in the column and pushes the fields below it down, so a
          click on the next field reaches that field and never a row laid over it. */}
      {listOpen && (
        <div
          id={listId}
          role="listbox"
          aria-label={`${label} options`}
          style={{
            marginTop: 4,
            background: 'var(--raven-surface-tier1)', border: '1px solid var(--raven-divider)',
            borderRadius: 'var(--raven-radius-menu)', boxShadow: 'var(--raven-shadow-raised)',
            maxHeight: 220, overflowY: 'auto',
          }}
        >
          {groups.map((group) => {
            const groupRows = rows.filter((row) => row.kind === 'option' && row.group === group.name);
            if (!groupRows.length) return null;
            return (
              <div key={group.name} role="group" aria-label={group.name}>
                <div style={{ padding: '6px 10px 2px', fontSize: 10, fontWeight: 600, color: 'var(--raven-text-secondary)', textTransform: 'uppercase' }}>
                  {group.name}
                </div>
                {groupRows.map((row) => {
                  if (row.kind !== 'option') return null;
                  const idx = ++rowIdx;
                  return (
                    <div
                      key={`${row.option.kind}-${row.option.ref}-${row.option.pnid ?? ''}`}
                      id={optionId(idx)}
                      role="option"
                      aria-selected={idx === activeIdx}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => pick(row)}
                      onMouseEnter={() => { setHoveredIdx(idx); onHighlightEntity?.(row.option.id ?? null); }}
                      onMouseLeave={() => { setHoveredIdx(null); onHighlightEntity?.(null); }}
                      style={{ ...rowStyle(idx === activeIdx || idx === hoveredIdx), fontWeight: row.option.ref === value ? 600 : 400 }}
                    >
                      <div>{row.option.label}</div>
                      {row.option.detail && <div style={{ fontSize: 10, color: 'var(--raven-text-secondary)' }}>{row.option.detail}</div>}
                    </div>
                  );
                })}
              </div>
            );
          })}
          {rows.map((row) => row.kind === 'use' && (
            <div
              key="use"
              id={optionId(rows.length - 1)}
              role="option"
              aria-selected={activeIdx === rows.length - 1}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => pick(row)}
              onMouseEnter={() => setHoveredIdx(rows.length - 1)}
              onMouseLeave={() => setHoveredIdx(null)}
              style={{ ...rowStyle(activeIdx === rows.length - 1 || hoveredIdx === rows.length - 1), borderTop: '1px solid var(--raven-divider)' }}
            >
              <div style={{ fontWeight: 500 }}>Use "{row.text}"</div>
              <div style={{ fontSize: 10, color: 'var(--raven-text-secondary)' }}>Free text</div>
            </div>
          ))}
          {nodeRefMissing && (
            <div role="alert" style={{ padding: '8px 10px', fontSize: 11, color: 'var(--raven-error-text)', background: 'var(--raven-error-bg)' }}>
              {NODE_REF_MISSING}
            </div>
          )}
          {!rows.length && !nodeRefMissing && (
            <div style={{ padding: '8px 10px', fontSize: 11, color: 'var(--raven-text-secondary)' }}>Nothing to pick on this sheet yet.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default RefCombobox;
